// Fixed-position popup shown while one or more Twilio PSTN inbound
// calls are ringing. Mirrors the layout of the WhatsApp CallControl
// queue (stacked vertically on the right), so agents see the same UX
// pattern across call channels. Each queued call gets its own card
// with Accept / Reject. While the agent is on an active call, the
// Accept button is disabled (Twilio Voice SDK can't hold-and-answer
// a second call without extra hold/transfer plumbing).

import React from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { useTwilioVoice } from "src/context/twilioVoice/TwilioVoiceContext";
import useCallTone from "src/hooks/useCallTone";

const IncomingTwilioCallToast: React.FC = () => {
  const {
    hasIncoming,
    incomingQueue,
    isOnCall,
    acceptIncoming,
    rejectIncoming,
  } = useTwilioVoice();

  // Ringtone plays only when there are queued calls AND the agent
  // isn't already on a call. Beeping over an active conversation is
  // disruptive — the queue cards stay visible regardless, so the
  // agent still sees who's waiting (visual is the missed-call safety
  // net while the audio is silenced).
  useCallTone(hasIncoming && !isOnCall ? "incoming" : "idle");

  if (!hasIncoming) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        // When on an active call, slide the queue down below the
        // ActiveCallWidget (which sits at top: 80 and is ~320px tall
        // including DTMF margin). Otherwise the modals get covered.
        top: isOnCall ? 410 : 80,
        right: 20,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        // Shrink the available scroll region when we're slid down so
        // the cards never extend past the viewport.
        maxHeight: isOnCall ? "calc(100vh - 440px)" : "calc(100vh - 120px)",
        overflowY: "auto",
        pr: "8px",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(0,0,0,0.1)",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "rgba(0,0,0,0.2)",
        },
      }}
    >
      {incomingQueue.map((entry) => {
        const hasName = Boolean(entry.customerName);
        const primaryLine = hasName
          ? entry.customerName
          : entry.from || "Unknown caller";
        const secondaryLine = hasName ? entry.from : null;
        return (
          <Paper
            key={entry.callSid}
            elevation={6}
            sx={{
              p: 2,
              width: 300,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.97)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(242, 47, 70, 0.18)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: "transform 0.2s ease",
              position: "relative",
              "&:hover": { transform: "translateX(-4px)" },
            }}
          >
            <Chip
              label="Twilio"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                height: 18,
                fontSize: 10,
                color: "white",
                background: "#F22F46",
              }}
            />
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mb: 1,
                border: "2px solid #F22F46",
                bgcolor: "rgba(242, 47, 70, 0.08)",
              }}
            >
              <LocalPhoneIcon sx={{ color: "#F22F46", fontSize: 28 }} />
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {primaryLine}
            </Typography>
            {secondaryLine && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.55)", fontFamily: "monospace" }}
              >
                {secondaryLine}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mb: 1.5 }}
            >
              {isOnCall ? "Waiting (busy on another call)" : "Incoming call"}
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Tooltip
                title={
                  isOnCall ? "Hang up the current call first" : "Accept call"
                }
              >
                <span>
                  <IconButton
                    onClick={() => acceptIncoming(entry.callSid)}
                    disabled={isOnCall}
                    sx={{
                      bgcolor: isOnCall ? "#cccccc" : "#2e7d32",
                      color: "white",
                      "&:hover": { bgcolor: isOnCall ? "#cccccc" : "#256528" },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <CallIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject call">
                <IconButton
                  onClick={() => rejectIncoming(entry.callSid)}
                  sx={{
                    bgcolor: "#FF3B30",
                    color: "white",
                    "&:hover": { bgcolor: "#D70015" },
                    width: 48,
                    height: 48,
                  }}
                >
                  <CallEndIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default IncomingTwilioCallToast;
